import { useState } from "react";
import Turnstile from "react-turnstile";
import { type SpaceTokens, View } from "@medbillai/ui";
import { analyticsService } from "../../../lib/analytics/analytics";
import { useBotControl } from "./BotControlProvider";

// Cloudflare Turnstile test keys. For local dev, BOTH the Next sitekey
// (NEXT_PUBLIC_BOT_CONTROL_KEY) and the API secret (BOT_CONTROL_SECRET_KEY) must
// be set to test keys from below. They needn't match positionally, but if one
// side is a real key and the other a test key, siteverify returns
// `invalid-input-response` and login fails with "Bot control inspection failed".
// The secret decides pass/fail; the sitekey decides the widget. For a hidden,
// always-pass login use the invisible 1x…BB sitekey with the 1x…AA secret.
// See ts/apps/api/CLAUDE.md -> "Bot control".
// https://developers.cloudflare.com/turnstile/troubleshooting/testing/
//
// NEXT_PUBLIC_BOT_CONTROL_KEY (sitekey — widget behavior):
//   1x00000000000000000000AA  visible,   always passes
//   2x00000000000000000000AB  visible,   always fails
//   3x00000000000000000000FF  visible,   forces interactive challenge
//   1x00000000000000000000BB  invisible, always passes
//   2x00000000000000000000BB  invisible, always fails
// BOT_CONTROL_SECRET_KEY (API secret — pass/fail verdict):
//   1x0000000000000000000000000000000AA  always passes
//   2x0000000000000000000000000000000AA  always fails
//   3x0000000000000000000000000000000AA  "token already spent"
const botControlKey = process.env.NEXT_PUBLIC_BOT_CONTROL_KEY ?? "";
const suppressedErrorPrefixes = ["102",
// Invalid Parameters (retryable)
"103",
// Invalid Parameters (retryable)
"104",
// Invalid Parameters (retryable)
"106",
// Invalid Parameters (retryable)
"11060",
// Challenge timed out (retryable)
"11062",
// Challenge timed out (visible mode, retryable)
"300",
// Generic client execution error (retryable)
"600" // Challenge execution failure (retryable)
];

// https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
// If an error callback returns with a non-falsy result, Turnstile will
// assume that the error callback handled the error accordingly.
const onBotControlError = (errorCode: string) => {
  analyticsService.trackEvent("BotControlChallengeError", {
    errorCode
  });

  // Suppress default error handling (which would normally throw an error and result in a sentry alert)
  if (suppressedErrorPrefixes.some(prefix => errorCode.startsWith(prefix))) {
    console.warn(`Suppressed bot control error: ${errorCode}`);
    return true;
  }
  console.error(`Bot control turnstile error: ${errorCode}`);
  return false;
};
const onBotControlLoaded = () => {
  analyticsService.trackEvent("BotControlChallengeIssued", {});
};
type BotControlProps = {
  interactiveMargin: SpaceTokens;
};
export const BotControl = ({
  interactiveMargin
}: BotControlProps) => {
  const {
    onBotControlVerified,
    onBotControlExpired,
    isBotControlEnabled
  } = useBotControl();
  const [isInteractive, setIsInteractive] = useState(false);
  if (!isBotControlEnabled) {
    return null;
  }
  return (
    // The turnstile still takes up space even when hidden, so we need to manually hide it with a height of 0
    <View marginVertical={isInteractive ? interactiveMargin : 0} height={isInteractive ? undefined : 0} data-sentry-element="View" data-sentry-component="BotControl" data-sentry-source-file="BotControl.tsx">
      <Turnstile theme="auto"
      // biome-ignore lint/plugin/no-fixed-style-props: react-turnstile's size is a widget mode, not a Tamagui token
      size="flexible" appearance="interaction-only" refreshExpired="auto" sitekey={botControlKey} onBeforeInteractive={() => {
        analyticsService.trackEvent("BotControlChallengeInteractive", {});
        setIsInteractive(true);
      }} onVerify={onBotControlVerified} onExpire={onBotControlExpired} onError={onBotControlError} onLoad={onBotControlLoaded} data-sentry-element="Turnstile" data-sentry-source-file="BotControl.tsx" />
    </View>
  );
};